import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { usePageState } from "@/hooks/usePageState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { pageStateQueryOptions, PageAccessError } from "@/lib/queries";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";

/**
 * Post-Flight layout route. The page permission is checked before the outlet
 * renders. A denied page uses the shared no-access treatment.
 */
export const Route = createFileRoute("/_authenticated/post-flight")({
  beforeLoad: () => assertPagePermission("post-flight"),
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData(pageStateQueryOptions("post-flight"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      }),
  component: PostFlightLayout,
  errorComponent: PageAccessErrorComponent,
});

function PostFlightLayout() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.postFlight", "Post-Flight"));
  const { isLoading, error } = usePageState("post-flight");

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        {t("common.loading", "Loading…")}
      </div>
    );
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <Outlet />;
}
